import React, { useState } from 'react';
import { Button, Input, PageBody, PageIntro, PageTitle, Paper } from 'src/components';
import { useAlerts } from 'src/common/AlertManager';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { reissueCallbacks } from 'src/api/functions/callbacksApi';
import styles from './ReissueCallbacks.module.scss';

const ReissueCallbacks: React.FC = () => {
  const accessToken = useAccessToken();
  const { showSuccess, showAlert } = useAlerts();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const formatDateToUTC = (dateTimeLocal: string): string => {
    if (!dateTimeLocal) return '';
    // Convert datetime-local string to UTC ISO string
    const date = new Date(dateTimeLocal);
    // Format as YYYY-MM-DDTHH:mm:ssZ
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
  };

  const handleStart = async () => {
    if (!startDate || !endDate) {
      showAlert('Please provide both start date and end date');
      return;
    }

    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);

    if (startDateObj >= endDateObj) {
      showAlert('Start date must be before end date');
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        start_date: formatDateToUTC(startDate),
        end_date: formatDateToUTC(endDate),
      };

      const result = await reissueCallbacks(accessToken, payload);

      // Check if result has a status property
      if ('status' in result) {
        const status = result.status;
        if (status >= 200 && status < 300) {
          // Success response (2xx status codes including 202)
          showSuccess('Callbacks reissue started successfully');
          setStartDate('');
          setEndDate('');
        } else {
          // Error response (status >= 400) - result should be ErrorResponseResult
          const errorResult = result as { error?: string };
          showAlert(errorResult.error || 'Failed to reissue callbacks. Please try again.');
        }
      } else if ('error' in result) {
        // Error response without status
        showAlert(result.error || 'Failed to reissue callbacks. Please try again.');
      } else {
        // Default success case (no error, no status) - normal data response
        showSuccess('Callbacks reissue started successfully');
        setStartDate('');
        setEndDate('');
      }
    } catch (error: any) {
      showAlert(error?.message || 'An error occurred while reissuing callbacks.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <PageTitle title="Re-issue Callbacks" className={styles.pageTitle} />
      <PageIntro inset overview="Replay the upload and processing notifications sent to your endpoint over a chosen period. Use it when your receiving system was down or mid-deploy and missed them." />
      <PageBody narrow>
        <Paper>
          <p className={styles.description}>
            Provide a start time and end time to reissue callbacks for the specified date range.
          </p>

          <div className={styles.form}>
            <Input
              type="datetime-local"
              label="Start Date"
              value={startDate}
              onChange={setStartDate}
              step={1}
              className={styles.input}
            />
            <Input
              type="datetime-local"
              label="End Date"
              value={endDate}
              onChange={setEndDate}
              step={1}
              className={styles.input}
            />

            <div className={styles.buttonContainer}>
              <Button
                onClick={handleStart}
                disabled={!startDate || !endDate || isLoading}
                loading={isLoading}
              >
                Start
              </Button>
            </div>
          </div>
        </Paper>
      </PageBody>
    </div>
  );
};

export default ReissueCallbacks;

